import { Component, OnInit, OnChanges, SimpleChanges, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { MappingService } from '../../services/mapping.service';
import _moment from 'moment';

@Component({
  selector: 'app-ndvi-chart',
  templateUrl: './ndvi-chart.component.html',
  styleUrls: ['./ndvi-chart.component.css'],
  standalone: false
})
export class NdviChartComponent implements OnInit, OnChanges {
  @Input() fieldId: string = '';
  
  chartOption: EChartsOption = {};
  hasData: boolean = false;
  private ndviDate = [];
  private ndviMinSeries = [];
  private ndviMaxSeries = [];
  private ndviMeanSeries = [];
  private fromDate = _moment().subtract(6, 'months').format('YYYY-MM-DD');
  private toDate = _moment().format('YYYY-MM-DD');

  constructor(private mappingService: MappingService) {}

  ngOnInit(): void {
    if (this.fieldId) {
      this.loadNdviData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldId'] && !changes['fieldId'].firstChange && this.fieldId) {
      this.loadNdviData();
    }
  }

  loadNdviData(): void {
    this.mappingService.getNdviChart(Number(this.fieldId), this.fromDate, this.toDate).subscribe((ndviTmpData) => {
      this.ndviDate = [];
      this.ndviMinSeries = [];
      this.ndviMaxSeries = [];
      this.ndviMeanSeries = [];
      
      this.hasData = ndviTmpData && ndviTmpData.length > 0;
      
      ndviTmpData.forEach(ndviData => {
        const min = ndviData.min;
        const max = ndviData.max;
        const mean = ndviData.mean.toFixed(2);
        const date = _moment(ndviData.lastModified).format('DD MMM YY');
        
        this.ndviDate.push(date.toString());
        this.ndviMinSeries.push(min);
        this.ndviMaxSeries.push(max);
        this.ndviMeanSeries.push(mean);
      });

      this.chartOption = {
          title: {
            text: 'NDVI Index',
            left: 'center',
            top: 10,
            textStyle: {
              fontSize: 18,
              fontWeight: 'bold'
            }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            }
          },
          legend: {
            data: ['Min', 'Max', 'Mean'],
            right: 10,
            top: 40
          },
          grid: {
            left: '10%',
            right: '5%',
            bottom: '15%',
            top: '25%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: this.ndviDate,
            axisTick: {
              show: false
            },
            axisLabel: {
              rotate: 90,
              interval: 0,
              formatter: (value, index) => {
                const totalLabels = this.ndviDate.length;
                let skipInterval;
                if (totalLabels > 180) {
                  skipInterval = Math.ceil(totalLabels / 20);
                } else if (totalLabels > 90) {
                  skipInterval = Math.ceil(totalLabels / 25);
                } else if (totalLabels > 60) {
                  skipInterval = Math.ceil(totalLabels / 30);
                } else if (totalLabels > 30) {
                  skipInterval = Math.ceil(totalLabels / 20);
                } else {
                  skipInterval = 1;
                }
                return index % skipInterval === 0 ? value : '';
              },
              hideOverlap: true
            }
          },
          yAxis: {
            type: 'value',
            min: 0,
            max: 1,
            axisLabel: {
              formatter: '{value}'
            }
          },
          series: [
            {
              name: 'Min',
              type: 'line',
              data: this.ndviMinSeries,
              itemStyle: { color: '#663333' },
              lineStyle: {
                width: 2,
                color: '#663333'
              },
              symbol: 'circle',
              symbolSize: 0,
              showSymbol: false,
              emphasis: {
                focus: 'series',
                itemStyle: {
                  borderColor: '#663333',
                  borderWidth: 2
                }
              }
            },
            {
              name: 'Max',
              type: 'line',
              data: this.ndviMaxSeries,
              itemStyle: { color: '#0000CC' },
              lineStyle: {
                width: 2,
                color: '#0000CC'
              },
              symbol: 'circle',
              symbolSize: 0,
              showSymbol: false,
              emphasis: {
                focus: 'series',
                itemStyle: {
                  borderColor: '#0000CC',
                  borderWidth: 2
                }
              }
            },
            {
              name: 'Mean',
              type: 'line',
              data: this.ndviMeanSeries,
              itemStyle: { color: '#8dc63f' },
              lineStyle: {
                width: 2,
                color: '#8dc63f'
              },
              symbol: 'circle',
              symbolSize: 0,
              showSymbol: false,
              emphasis: {
                focus: 'series',
                itemStyle: {
                  borderColor: '#8dc63f',
                  borderWidth: 2
                }
              }
            }
          ]
        };
    });
  }
}
